// components/Pagination.tsx
'use client';

import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import ArrowLeftSLineIcon from 'remixicon-react/ArrowLeftSLineIcon';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    slug?: string;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, slug }) => {
    const postsContainerRef = useRef<HTMLDivElement>(null);
    const [paginationClicked, setPaginationClicked] = useState<boolean>(false);

    useEffect(() => {
        if (paginationClicked) {
            const timer = setTimeout(() => {
                if (postsContainerRef.current) {
                    postsContainerRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);

            return () => clearTimeout(timer);
        }

        setPaginationClicked(false);
    }, [currentPage, paginationClicked]);

    if (totalPages <= 1) return null;

    const renderPaginationControls = () => {
        const pages: (number | string)[] = [];

        pages.push(1);

        const rangeStart = Math.max(2, currentPage - 1);
        const rangeEnd = Math.min(currentPage + 1, totalPages - 1);

        if (rangeStart > 2) pages.push('...');
        for (let i = rangeStart; i <= rangeEnd; i++) {
            if (i !== 1 && i !== totalPages) pages.push(i);
        }
        if (rangeEnd < totalPages - 1) pages.push('...');
        if (totalPages > 1 && !pages.includes(totalPages)) pages.push(totalPages);

        return pages.map((page, index) => {
            if (page === '...') {
                return (
                    <span key={index} className='text-primary dark:text-white text-opacity-80'>
                        …
                    </span>
                );
            }

            if (page === currentPage) {
                return (
                    <span
                        key={index}
                        className='size-9 flex justify-center items-center rounded-xl bg-progress text-white'
                    >
                        {page}
                    </span>
                );
            }

            // Numbered Buttons Logic (Already correct, but ensuring consistency)
            const href = page === 1 ? `/${slug}` : `/${slug}/page/${page}`;

            return (
                <Link
                    key={index}
                    href={href}
                    onClick={() => setPaginationClicked(true)}
                    className='size-9 flex justify-center items-center rounded-xl bg-gray-100 text-primary hover:bg-progress hover:text-white transition-all'
                >
                    {page}
                </Link>
            );
        });
    };

    return (
        <div className='flex gap-2 text-sm items-center justify-center customContainer py-5'>
            {/* Prev Button Fix */}
            {currentPage > 1 && (
                <Link
                    // 👇 If prev page is 1, go to root slug. Else go to page/X
                    href={currentPage - 1 === 1 ? `/${slug}` : `/${slug}/page/${currentPage - 1}`}
                    onClick={() => {
                        setPaginationClicked(true);
                    }}
                    className='p-1.5 rounded-xl bg-bg border dark:bg-white/5 dark:border-white/10 text-progress dark:text-white/80 dark:hover:text-white'
                    aria-label='Previous Page'
                >
                    <ArrowRightSLineIcon />
                </Link>
            )}

            {renderPaginationControls()}

            {currentPage < totalPages && (
                <Link
                    href={`/${slug}/page/${currentPage + 1}`}
                    onClick={() => {
                        setPaginationClicked(true);
                    }}
                    className='p-1.5 rounded-xl bg-bg border dark:bg-white/5 dark:border-white/10 text-progress dark:text-white/80 dark:hover:text-white'
                    aria-label='Next Page'
                >
                    <ArrowLeftSLineIcon />
                </Link>
            )}
        </div>
    );
};

export default Pagination;
